/**
 * Edit Health Record Page - Edit existing health record entries
 * Mapped to backend HealthRecordDTO with exact field names
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Calendar, FileText } from 'lucide-react';
import { healthRecordsApi, type HealthRecord } from '../../services/gaushala/api';

interface HealthRecordFormData {
  cattleId: number;
  recordType: string;
  recordDate: string;
  veterinarianName: string;
  veterinarianLicense: string;
  veterinarianContact: string;
  diagnosis: string;
  treatment: string;
  medications: string;
  dosageInstructions: string;
  vaccinationType: string;
  nextVaccinationDate: string;
  nextCheckupDate: string;
  cost: number;
  notes: string;
  performedBy: string;
  status: string;
}

// Reusable form field components - defined outside main component to prevent recreation
const InputField = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  error,
  className = '',
  ...props
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={type === 'number' ? (value || '') : value}
      onChange={(e) => {
        if (type === 'number') {
          const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
          onChange(isNaN(numValue) ? 0 : numValue);
        } else {
          onChange(e.target.value);
        }
      }}
      placeholder={placeholder}
      className={`w-full px-3 py-2 bg-white border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        type === 'number' ? '' : 'transition-all duration-200'
      } ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
      {...props}
    />
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  options,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value) || e.target.value)}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    >
      <option value={0}>{placeholder}</option>
      {options.map((option: any) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

const TextAreaField = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  rows = 3,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-vertical ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    />
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <span className="text-red-500">⚠</span>
        {error}
      </div>
    )}
  </div>
);

export default function EditHealthRecord() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // TODO: Fetch these from backend APIs when available
  const [cattle, setCattle] = useState<any[]>([]);

  const [formData, setFormData] = useState<HealthRecordFormData>({
    cattleId: 0,
    recordType: '',
    recordDate: '',
    veterinarianName: '',
    veterinarianLicense: '',
    veterinarianContact: '',
    diagnosis: '',
    treatment: '',
    medications: '',
    dosageInstructions: '',
    vaccinationType: '',
    nextVaccinationDate: '',
    nextCheckupDate: '',
    cost: 0,
    notes: '',
    performedBy: '',
    status: ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof HealthRecordFormData, string>>>({});

  useEffect(() => {
    // TODO: Fetch cattle from backend
    // For now using mock data
    setCattle([
      { id: 1, name: 'Cow 001 - Ganga' },
      { id: 2, name: 'Cow 002 - Yamuna' },
      { id: 3, name: 'Cow 003 - Saraswati' }
    ]);
  }, []);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const result = await healthRecordsApi.getHealthRecordById(parseInt(id));

        if (result.success && result.data) {
          const record = result.data;
          // Convert ISO date strings to YYYY-MM-DD format for date inputs
          const formatDateForInput = (dateString?: string) => {
            if (!dateString) return '';
            try {
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
            } catch {
              return '';
            }
          };

          setFormData({
            cattleId: record.cattleId || 0,
            recordType: record.recordType || '',
            recordDate: formatDateForInput(record.recordDate),
            veterinarianName: record.veterinarianName || '',
            veterinarianLicense: record.veterinarianLicense || '',
            veterinarianContact: record.veterinarianContact || '',
            diagnosis: record.diagnosis || '',
            treatment: record.treatment || '',
            medications: record.medications || '',
            dosageInstructions: record.dosageInstructions || '',
            vaccinationType: record.vaccinationType || '',
            nextVaccinationDate: formatDateForInput(record.nextVaccinationDate),
            nextCheckupDate: formatDateForInput(record.nextCheckupDate),
            cost: record.cost || 0,
            notes: record.notes || '',
            performedBy: record.performedBy || '',
            status: record.status || ''
          });
        } else {
          setMessage({
            type: 'error',
            text: result.error || 'Failed to load health record.'
          });
        }
      } catch (error) {
        console.error('Error fetching health record:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load health record.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id]);

  const handleInputChange = (field: keyof HealthRecordFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof HealthRecordFormData, string>> = {};

    if (formData.cattleId === 0) newErrors.cattleId = 'Cattle is required';
    if (!formData.recordDate.trim()) newErrors.recordDate = 'Record Date is required';
    if (!formData.recordType.trim()) newErrors.recordType = 'Record Type is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields.'
      });
      return;
    }

    if (!id) {
      setMessage({
        type: 'error',
        text: 'Invalid health record ID.'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const healthRecordData: Omit<HealthRecord, 'id' | 'createdBy' | 'updatedBy' | 'isActive' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
        cattleId: formData.cattleId,
        recordType: formData.recordType,
        recordDate: formData.recordDate ? new Date(formData.recordDate).toISOString().split('T')[0] : '',
        veterinarianName: formData.veterinarianName || undefined,
        veterinarianLicense: formData.veterinarianLicense || undefined,
        veterinarianContact: formData.veterinarianContact || undefined,
        diagnosis: formData.diagnosis || undefined,
        treatment: formData.treatment || undefined,
        medications: formData.medications || undefined,
        dosageInstructions: formData.dosageInstructions || undefined,
        vaccinationType: formData.vaccinationType || undefined,
        nextVaccinationDate: formData.nextVaccinationDate ? new Date(formData.nextVaccinationDate).toISOString().split('T')[0] : undefined,
        nextCheckupDate: formData.nextCheckupDate ? new Date(formData.nextCheckupDate).toISOString().split('T')[0] : undefined,
        cost: formData.cost || undefined,
        notes: formData.notes || undefined,
        performedBy: formData.performedBy || undefined,
        status: formData.status || undefined
      };

      console.log('Updating health record:', healthRecordData);
      const result = await healthRecordsApi.updateHealthRecord(parseInt(id), healthRecordData);

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Health record updated successfully!'
        });

        // Navigate back after delay
        setTimeout(() => {
          navigate('/gaushala/health-history');
        }, 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to update health record.'
        });
      }
    } catch (error) {
      console.error('Error updating health record:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update health record. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/health-history');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading health record...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Health Record</h1>
          <p className="text-gray-600 mt-1">Update health record #{id}</p>
        </div>
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <span className="text-green-500">✓</span>
            ) : (
              <span className="text-red-500">⚠</span>
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            Edit Health Record Information
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cattle */}
            <SelectField
              label="Cattle"
              value={formData.cattleId}
              onChange={(value: number) => handleInputChange('cattleId', value)}
              placeholder="Select cattle"
              required
              error={errors.cattleId}
              options={cattle}
            />

            {/* Record Type */}
            <InputField
              label="Record Type"
              value={formData.recordType}
              onChange={(value: string) => handleInputChange('recordType', value)}
              placeholder="e.g., VACCINATION, TREATMENT, CHECKUP"
              required
              error={errors.recordType}
            />

            {/* Record Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">
                Record Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.recordDate}
                  onChange={(e) => handleInputChange('recordDate', e.target.value)}
                  className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer ${
                    errors.recordDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.recordDate && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.recordDate}
                </div>
              )}
            </div>

            {/* Status */}
            <InputField
              label="Status"
              value={formData.status}
              onChange={(value: string) => handleInputChange('status', value)}
              placeholder="e.g., SCHEDULED, COMPLETED, CANCELLED"
              error={errors.status}
            />

            {/* Veterinarian Name */}
            <InputField
              label="Veterinarian Name"
              value={formData.veterinarianName}
              onChange={(value: string) => handleInputChange('veterinarianName', value)}
              placeholder="Veterinarian name"
              error={errors.veterinarianName}
            />

            {/* Veterinarian License */}
            <InputField
              label="Veterinarian License"
              value={formData.veterinarianLicense}
              onChange={(value: string) => handleInputChange('veterinarianLicense', value)}
              placeholder="License number"
              error={errors.veterinarianLicense}
            />

            {/* Veterinarian Contact */}
            <InputField
              label="Veterinarian Contact"
              value={formData.veterinarianContact}
              onChange={(value: string) => handleInputChange('veterinarianContact', value)}
              placeholder="Contact number"
              error={errors.veterinarianContact}
            />

            {/* Diagnosis */}
            <InputField
              label="Diagnosis"
              value={formData.diagnosis}
              onChange={(value: string) => handleInputChange('diagnosis', value)}
              placeholder="Diagnosis details"
              error={errors.diagnosis}
            />

            {/* Treatment */}
            <InputField
              label="Treatment"
              value={formData.treatment}
              onChange={(value: string) => handleInputChange('treatment', value)}
              placeholder="Treatment provided"
              error={errors.treatment}
            />

            {/* Medications */}
            <InputField
              label="Medications"
              value={formData.medications}
              onChange={(value: string) => handleInputChange('medications', value)}
              placeholder="Medication names"
              error={errors.medications}
            />

            {/* Dosage Instructions */}
            <InputField
              label="Dosage Instructions"
              value={formData.dosageInstructions}
              onChange={(value: string) => handleInputChange('dosageInstructions', value)}
              placeholder="e.g., 500mg twice daily"
              error={errors.dosageInstructions}
            />

            {/* Vaccination Type */}
            <InputField
              label="Vaccination Type"
              value={formData.vaccinationType}
              onChange={(value: string) => handleInputChange('vaccinationType', value)}
              placeholder="Type of vaccine"
              error={errors.vaccinationType}
            />

            {/* Next Vaccination Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">Next Vaccination Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.nextVaccinationDate}
                  onChange={(e) => handleInputChange('nextVaccinationDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Next Checkup Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-800">Next Checkup Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.nextCheckupDate}
                  onChange={(e) => handleInputChange('nextCheckupDate', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 cursor-pointer"
                />
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Cost */}
            <InputField
              label="Cost (₹)"
              value={formData.cost}
              onChange={(value: number) => handleInputChange('cost', value)}
              type="number"
              placeholder="Treatment cost"
              error={errors.cost}
              min="0"
              step="0.01"
            />

            {/* Performed By */}
            <InputField
              label="Performed By"
              value={formData.performedBy}
              onChange={(value: string) => handleInputChange('performedBy', value)}
              placeholder="Person who performed the procedure"
              error={errors.performedBy}
            />

            {/* Notes */}
            <TextAreaField
              label="Notes"
              value={formData.notes}
              onChange={(value: string) => handleInputChange('notes', value)}
              placeholder="Additional notes or observations"
              className="md:col-span-2"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-start gap-4 mt-8 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {isLoading ? 'Updating...' : 'Update Health Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
